import { useState, useEffect } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { useVibrate } from '../../hooks/useVibrate'

/**
 * Componente toast per avviso registrazione duplicata
 * 
 * @param {Object} duplicate - Dati della registrazione esistente
 * @param {string} duplicate.minoreName - Nome del minore
 * @param {string} duplicate.educatoreName - Nome educatore che ha già registrato
 * @param {Date} duplicate.timestamp - Quando è stata fatta
 * @param {Function} onDismiss - Callback quando si chiude
 */
export function DuplicateWarning({ duplicate, onDismiss, duration = 10000 }) {
  const [visible, setVisible] = useState(true)
  const [exiting, setExiting] = useState(false)
  const { vibrateWarning } = useVibrate()

  useEffect(() => {
    if (duplicate) {
      setVisible(true)
      setExiting(false)
      // Vibra quando appare
      vibrateWarning()
      
      // Auto-dismiss dopo duration
      const timer = setTimeout(() => {
        handleDismiss()
      }, duration)
      
      return () => clearTimeout(timer)
    }
  }, [duplicate, duration, vibrateWarning])

  const handleDismiss = () => {
    setExiting(true)
    setTimeout(() => {
      setVisible(false)
      onDismiss?.()
    }, 300)
  }

  if (!visible || !duplicate) return null

  return (
    <div 
      className={`
        fixed bottom-20 right-4 left-4 sm:left-auto sm:w-96 z-50
        transition-all duration-300
        ${exiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}
      `}
    >
      <div className="bg-yellow-50 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800 rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
              ⚠️ Registrazione già presente
            </h4>
            <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
              <strong>{duplicate.minoreName}</strong> ha già una registrazione di oggi
              {duplicate.educatoreName && (
                <> inserita da <strong>{duplicate.educatoreName}</strong></>
              )}
            </p>
            {duplicate.timestamp && (
              <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                Alle {new Date(duplicate.timestamp).toLocaleTimeString('it-IT', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </p>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 text-yellow-500 hover:text-yellow-700 dark:hover:text-yellow-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default DuplicateWarning
